import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCalendarDay } from '@react-icons/all-files/fa/FaCalendarDay';
import { FaUmbrellaBeach } from '@react-icons/all-files/fa/FaUmbrellaBeach';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../../commons/Breadcrumb';
import PageHeader from '../../../../../commons/PageHeader';
import PageForm, { FormField, SelectOption } from '../../../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../../manager/calendario-dia-festivo.reducer';

const BACK = '/configuracion/calendario/feriados/calendario-festivos';
const FERIADOS = '/configuracion/calendario/feriados';

type TipoRegla = 'fecha' | 'semana';

interface CalendarioFestivoForm {
  id: string;
  nombre: string;
  tipo: TipoRegla | '';
  mes: string;
  semana: string;
  dia: string;
}

const empty: CalendarioFestivoForm = {
  id: '',
  nombre: '',
  tipo: 'fecha',
  mes: '',
  semana: '',
  dia: '',
};

const TIPO_OPTIONS: SelectOption[] = [
  { value: 'fecha', label: 'Fecha fija (mes + día del mes)' },
  { value: 'semana', label: 'Por semana (ordinal + día de la semana)' },
];

const MES_OPTIONS: SelectOption[] = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const SEMANA_OPTIONS: SelectOption[] = [
  { value: 1, label: 'Primera semana' },
  { value: 2, label: 'Segunda semana' },
  { value: 3, label: 'Tercera semana' },
  { value: 4, label: 'Cuarta semana' },
  { value: 5, label: 'Quinta semana' },
];

const DIA_SEMANA_OPTIONS: SelectOption[] = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

export default function CalendarioFestivosFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.calendarioDiaFestivo.entity) as any;
  const account = useAppSelector(s => s.authentication.account) as any;
  const updating = useAppSelector(s => s.calendarioDiaFestivo.updating);
  const updateSuccess = useAppSelector(s => s.calendarioDiaFestivo.updateSuccess);
  const errorMessage = useAppSelector(s => s.calendarioDiaFestivo.errorMessage);

  const [form, setForm] = useState<CalendarioFestivoForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof CalendarioFestivoForm, string>>>({});

  const { alert, showToast } = usePageAlert();

  const esTipoSemana = form.tipo === 'semana';

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      const tipo = String(entity.tipo ?? 'fecha').toLowerCase() as TipoRegla;
      setForm({
        id: String(entity.id),
        nombre: entity.nombre ?? '',
        tipo: tipo === 'semana' ? 'semana' : 'fecha',
        mes: entity.mes != null ? String(entity.mes) : '',
        semana: entity.semana != null ? String(entity.semana) : '',
        dia: entity.dia != null ? String(entity.dia) : '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Regla de calendario creada correctamente.'
            : 'Regla de calendario actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  const handleFieldChange = (name: keyof CalendarioFestivoForm, value: string) => {
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'tipo' && value === 'fecha') next.semana = '';
      return next;
    });
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const buildPayload = () => {
    const now = new Date().toISOString();
    const userId = account?.id;
    const base = {
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      mes: Number(form.mes),
      dia: Number(form.dia),
      semana: esTipoSemana ? Number(form.semana) : null,
    };

    if (isNew) {
      return {
        ...base,
        created: now,
        createdBy: userId,
        updated: now,
        updatedBy: userId,
      };
    }

    return {
      ...entity,
      id: Number(form.id),
      ...base,
      updated: now,
      updatedBy: userId,
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof CalendarioFestivoForm, string>> = {};
    if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
    if (!form.tipo) errs.tipo = 'El tipo es requerido';
    if (!form.mes) errs.mes = 'El mes es requerido';
    if (!form.dia) errs.dia = esTipoSemana ? 'El día de la semana es requerido' : 'El día del mes es requerido';
    if (esTipoSemana && !form.semana) errs.semana = 'La semana es requerida';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = buildPayload();
    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  const fields: FormField<CalendarioFestivoForm>[] = useMemo(() => [
    {
      name: 'id',
      label: 'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name: 'nombre',
      label: 'Nombre',
      required: true,
      placeholder: 'Ej: Día de la Independencia, Día de la Bandera',
    },
    {
      name: 'tipo',
      label: 'Tipo de regla',
      type: 'select',
      required: true,
      options: TIPO_OPTIONS,
    },
    {
      name: 'mes',
      label: 'Mes',
      type: 'select',
      required: true,
      options: MES_OPTIONS,
    },
    ...(esTipoSemana ? [{
      name: 'semana' as const,
      label: 'Semana del mes',
      type: 'select' as const,
      required: true,
      options: SEMANA_OPTIONS,
    }] : []),
    esTipoSemana
      ? {
          name: 'dia',
          label: 'Día de la semana',
          type: 'select',
          required: true,
          options: DIA_SEMANA_OPTIONS,
          hint: 'Convención ISO: lunes = 1, domingo = 7.',
        }
      : {
          name: 'dia',
          label: 'Día del mes',
          type: 'number',
          required: true,
          placeholder: 'Ej: 16',
        },
  ], [esTipoSemana]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Feriados', to: FERIADOS, icon: <FaUmbrellaBeach /> },
        { label: 'Calendario de Festivos', to: BACK, icon: <FaCalendarDay /> },
        { label: isNew ? 'Nueva regla' : `Editar regla #${id}` },
      ]} />

      <PageHeader
        icon={<FaCalendarDay />}
        iconColor="#ef4444"
        title={isNew ? 'Nueva Regla de Calendario' : 'Editar Regla de Calendario'}
        subtitle={isNew
          ? 'Define una regla recurrente para generar días festivos anualmente'
          : `Modificando regla #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#ef4444"
      />
    </div>
  );
}
